import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { PriorityBadge, Avatar, StatusBadge } from "../badges";
import AttachmentsPanel from "../AttachmentsPanel";
import { toast } from "sonner";
import { Trash2, Link2, X } from "lucide-react";

const STATUSES = ["todo", "in_progress", "blocked", "testing", "completed"];

export default function TaskModal({ taskId, tasks, requirements, users, onClose, onChanged }) {
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [body, setBody] = useState("");
  const [depSelect, setDepSelect] = useState("none");
  const [depType, setDepType] = useState("FS");

  const load = () => {
    api.get(`/tasks/${taskId}`).then((r) => setTask(r.data));
    api.get("/comments", { params: { entity_type: "task", entity_id: taskId } }).then((r) => setComments(r.data));
    api.get("/activities", { params: { entity_type: "task", entity_id: taskId, limit: 30 } }).then((r) => setActivities(r.data));
  };
  useEffect(load, [taskId]); // eslint-disable-line

  if (!task) return null;

  const taskById = (id) => tasks.find((t) => t.id === id);
  const userById = (id) => users.find((u) => u.id === id);
  const req = requirements.find((r) => r.id === task.requirement_id);

  const patch = async (updates) => {
    try {
      await api.patch(`/tasks/${taskId}`, updates);
      load(); onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const addDep = () => {
    if (depSelect === "none") return;
    const deps = [...(task.dependencies || []), { task_id: depSelect, type: depType }];
    patch({ dependencies: deps });
    setDepSelect("none");
  };

  const removeDep = (tid) => patch({ dependencies: (task.dependencies || []).filter((d) => d.task_id !== tid) });

  const comment = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/comments", { entity_type: "task", entity_id: taskId, body });
      setBody(""); load(); onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/tasks/${taskId}`); toast.success("Task deleted"); onClose(); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="task-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-primary">{task.tid}</span>
            <StatusBadge status={task.status} />
            <PriorityBadge priority={task.priority} />
            {req && <span className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{req.rid}</span>}
          </div>
          <DialogTitle className="font-heading text-left">{task.title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{task.description || "No description."}</p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Status</Label>
            <Select value={task.status} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger data-testid="task-modal-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Assignee</Label>
            <Select value={task.assignee_id || "none"} onValueChange={(v) => patch({ assignee_id: v === "none" ? null : v })}>
              <SelectTrigger data-testid="task-modal-assignee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Start date</Label>
            <Input type="date" data-testid="task-modal-start-input" value={task.start_date || ""} onChange={(e) => patch({ start_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Due date</Label>
            <Input type="date" data-testid="task-modal-due-input" value={task.due_date || ""} onChange={(e) => patch({ due_date: e.target.value })} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Progress: {task.progress}%</Label>
          <Slider data-testid="task-modal-progress-slider" value={[task.progress]} max={100} step={5}
            onValueCommit={(v) => patch({ progress: v[0] })} />
        </div>

        <div className="border-t border-border pt-4 space-y-2">
          <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <Link2 className="h-3 w-3" /> Dependencies (predecessors)
          </Label>
          <div className="flex flex-wrap gap-2">
            {(task.dependencies || []).map((d) => (
              <span key={d.task_id} className="flex items-center gap-1 text-xs font-mono border border-border rounded px-2 py-1" data-testid="task-dependency-chip">
                {taskById(d.task_id)?.tid || "?"} <span className="text-primary">{d.type}</span>
                <button onClick={() => removeDep(d.task_id)} data-testid="remove-dependency-btn"><X className="h-3 w-3 text-muted-foreground hover:text-rose-500" /></button>
              </span>
            ))}
            {!(task.dependencies || []).length && <span className="text-xs text-muted-foreground">None</span>}
          </div>
          <div className="flex gap-2">
            <Select value={depSelect} onValueChange={setDepSelect}>
              <SelectTrigger className="flex-1" data-testid="add-dependency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select task...</SelectItem>
                {tasks.filter((t) => t.id !== taskId && !(task.dependencies || []).some((d) => d.task_id === t.id))
                  .map((t) => <SelectItem key={t.id} value={t.id}>{t.tid} — {t.title.slice(0, 40)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={depType} onValueChange={setDepType}>
              <SelectTrigger className="w-20" data-testid="add-dependency-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["FS", "SS", "FF"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" data-testid="add-dependency-btn" onClick={addDep}>Add</Button>
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <AttachmentsPanel entityType="task" entityId={taskId} compact />
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Discussion ({comments.length})</Label>
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2.5" data-testid="task-comment-item">
              <Avatar name={c.author_name} size="sm" />
              <div className="bg-accent/60 rounded-md px-3 py-2 flex-1 min-w-0">
                <p className="text-xs">
                  <span className="font-medium">{c.author_name}</span>{" "}
                  <span className="text-muted-foreground font-mono text-[10px]">{new Date(c.created_at).toLocaleString()}</span>
                </p>
                <p className="text-sm mt-0.5">{c.body}</p>
              </div>
            </div>
          ))}
          <Textarea data-testid="task-comment-input" placeholder="Add to the discussion... use @name to mention someone" value={body}
            onChange={(e) => setBody(e.target.value)} className="min-h-[60px]" />
          <Button size="sm" data-testid="task-comment-submit-btn" onClick={comment} disabled={!body.trim()}>Comment</Button>
        </div>

        <div className="border-t border-border pt-4 space-y-2">
          <Label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Activity History</Label>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {activities.map((a) => (
              <p key={a.id} className="text-xs text-muted-foreground" data-testid="task-activity-item">
                <span className="text-foreground font-medium">{a.actor_name}</span> {a.action}
                {a.detail && <span> — {a.detail}</span>}
                <span className="font-mono text-[10px]"> · {new Date(a.created_at).toLocaleString()}</span>
              </p>
            ))}
            {!activities.length && <p className="text-xs text-muted-foreground">No activity recorded.</p>}
          </div>
        </div>

        <div className="border-t border-border pt-3 flex justify-end">
          <Button variant="ghost" size="sm" data-testid="task-delete-btn" onClick={remove} className="text-rose-500 hover:text-rose-600">
            <Trash2 className="h-4 w-4 mr-1" /> Delete task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
